import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Truck, Package, CheckCircle, QrCode, Shapes, MagnifyingGlass, CalendarBlank, X, Receipt,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ScannerDialog } from "../components/dispatch/ScannerDialog";
import { MaterialSymbolsDialog } from "../components/dispatch/MaterialSymbolsDialog";
import { PartyDialog, SlipDialog } from "../components/dispatch/PortalDialogs";
import { PartyRow, RecordRow, Kpi, Empty } from "../components/dispatch/PortalRows";

const TABS = [
  { key: "send", label: "Material To Send", icon: Package },
  { key: "sent", label: "Material Sent", icon: CheckCircle },
  { key: "records", label: "Dispatch Records", icon: Receipt },
];

const useRecords = (q, date) => {
  const [rows, setRows] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => api.get("/dispatches", { params: { q: q || undefined, date: date || undefined } }).then((r) => setRows(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, date, tick]);
  return [rows, () => setTick((t) => t + 1)];
};

export default function WarehousePortal() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("send");
  const [party, setParty] = useState(null);
  const [slipId, setSlipId] = useState(null);
  const [slipToken, setSlipToken] = useState(null);
  const [scan, setScan] = useState(false);
  const [symbols, setSymbols] = useState(false);
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [records, reloadRecords] = useRecords(q, date);

  const load = () => api.get("/field-items", { params: { type: "inspection" } }).then((r) => {
    setItems(r.data);
    setParty((s) => (s ? r.data.find((x) => x.id === s.id) || null : null));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const ready = useMemo(() => items.filter((x) => ["approved", "dispatched", "install_submitted", "completed"].includes(x.status)), [items]);
  const toSend = ready.filter((x) => (x.dispatch_pending_total || 0) > 0);
  const sent = ready.filter((x) => x.dispatch_started);
  const term = q.trim().toLowerCase();
  const match = (x) => !term || [x.customer_name, x.customer_address, x.customer_mobile, x.text].some((v) => (v || "").toLowerCase().includes(term));
  const today = new Date().toLocaleDateString("en-CA");
  const todayCount = records.filter((r) => r.dispatch_date === today).length;

  const onScan = (text) => {
    const m = text.match(/\/d\/([\w-]+)/);
    if (m) { setSlipToken(m[1]); return; }
    if (/^DSP-/i.test(text)) { setTab("records"); setQ(text); return; }
    toast.error("This is not an Anuj Solar dispatch QR");
  };

  const onDispatched = async (evId) => {
    await load(); reloadRecords(); setParty(null); setSlipId(evId);
  };

  const list = tab === "send" ? toSend.filter(match) : tab === "sent" ? sent.filter(match) : null;

  return (
    <div className="max-w-6xl mx-auto space-y-6 stagger-in pb-16" data-testid="warehouse-portal">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-600">Warehouse · Logistics</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900 text-slate-900 flex items-center gap-2"><Truck size={32} weight="duotone" className="text-brand-500" /> Dispatch Control</h1>
          <p className="text-sm text-slate-500 mt-1">Pick a customer, add material, serials and vehicle — the slip is created automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="open-symbols-btn" variant="outline" className="h-11 rounded-full px-4" onClick={() => setSymbols(true)}><Shapes size={16} className="mr-1.5" /> Symbols</Button>
          <Button data-testid="scan-qr-btn" className="h-11 rounded-full px-5 bg-slate-900 hover:bg-slate-800 text-white font-bold" onClick={() => setScan(true)}><QrCode size={18} className="mr-1.5" /> Scan Slip QR</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="To Send" value={toSend.length} tone="amber" testid="kpi-to-send" />
        <Kpi label="Parties Served" value={sent.length} tone="emerald" testid="kpi-sent" />
        <Kpi label="Dispatches Today" value={todayCount} tone="sky" testid="kpi-today" />
        <Kpi label="Total Slips" value={records.length} tone="slate" testid="kpi-records" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.04)] overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-3 sm:p-4 border-b border-slate-200">
          <div className="flex gap-1 bg-slate-100 rounded-full p-1 overflow-x-auto" data-testid="portal-tabs">
            {TABS.map((t) => (
              <button key={t.key} type="button" data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`h-9 px-3 sm:px-4 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors ${tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                <t.icon size={15} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 lg:ml-auto">
            <div className="relative flex-1 lg:w-72">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="dispatch-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer, vehicle, Dispatch ID…"
                className="w-full h-10 pl-9 pr-8 rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:bg-white" />
              {q && <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setQ("")} data-testid="dispatch-search-clear"><X size={14} /></button>}
            </div>
            {tab === "records" && (
              <div className="relative">
                <CalendarBlank size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input data-testid="dispatch-search-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="h-10 pl-9 pr-2 rounded-full border border-slate-200 bg-slate-50 text-sm w-[150px]" />
              </div>
            )}
          </div>
        </div>

        <div className="p-3 sm:p-4">
          {list ? (
            list.length === 0 ? <Empty text={tab === "send" ? "Nothing pending to send. You are all caught up!" : "No material has been sent yet"} /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">{list.map((x) => <PartyRow key={x.id} x={x} kind={tab} onClick={() => setParty(x)} />)}</div>
            )
          ) : records.length === 0 ? <Empty text="No dispatch records found" /> : (
            <div className="space-y-2" data-testid="records-list">{records.map((r) => <RecordRow key={r.id} r={r} onClick={() => setSlipId(r.id)} />)}</div>
          )}
        </div>
      </div>

      {party && <PartyDialog x={party} onClose={() => setParty(null)} onDispatched={onDispatched} onViewSlip={(id) => setSlipId(id)} onChanged={load} />}
      {(slipId || slipToken) && <SlipDialog id={slipId} token={slipToken} onClose={() => { setSlipId(null); setSlipToken(null); }} />}
      <ScannerDialog open={scan} onClose={() => setScan(false)} onResult={onScan} wide={false} title="Scan Dispatch Slip QR" hint="Hold the slip QR code inside the frame" />
      <MaterialSymbolsDialog open={symbols} onClose={() => setSymbols(false)} />
    </div>
  );
}
